import React from 'react';
import { Check, X, Clock, Building2, Calendar } from 'lucide-react';
import './ShiftApprovalPanel.css';

interface ShiftApprovalWorker {
    readonly name: string;
    readonly initials: string;
    readonly avatarGradient: string;
    readonly role: string;
}

interface ShiftApprovalShift {
    readonly timeRange: string;
    readonly taskName: string;
    readonly location: string;
    readonly dateRange: string;
}

interface ShiftApprovalPanelProps {
    readonly isVisible: boolean;
    readonly worker: ShiftApprovalWorker;
    readonly shift: ShiftApprovalShift;
    readonly typedNotes: string;
    readonly isNotesFocused: boolean;
    readonly showSuccess: boolean;
}

/**
 * Floating approval panel used inside DemoAnimationOverlay.
 * Always rendered (never unmounted) so getBoundingClientRect
 * works on its data-demo-target elements even when hidden.
 */
const ShiftApprovalPanel = ({
    isVisible,
    worker,
    shift,
    typedNotes,
    isNotesFocused,
    showSuccess,
}: ShiftApprovalPanelProps): React.ReactElement => (
    <div className={`sap-panel ${isVisible ? 'sap-panel--visible' : 'sap-panel--hidden'}`}>

        {/* Worker */}
        <div className='sap-worker-row'>
            <div className='sap-avatar' style={{ background: worker.avatarGradient }}>
                {worker.initials}
            </div>
            <div className='sap-worker-info'>
                <span className='sap-worker-name'>{worker.name}</span>
                <span className='sap-worker-role'>{worker.role}</span>
            </div>
            <span className='sap-badge'>In Progress</span>
        </div>

        {/* Shift details */}
        <div className='sap-details'>
            <div className='sap-details-title'>Shift Details</div>

            <div className='sap-detail-item'>
                <span className='sap-detail-icon'><Clock size={9} /></span>
                <span className='sap-detail-text sap-detail-text--strong'>{shift.timeRange}</span>
            </div>

            <div className='sap-detail-item'>
                <span className='sap-detail-icon'><Building2 size={9} /></span>
                <span className='sap-detail-text sap-detail-text--strong'>{shift.taskName}</span>
                <span className='sap-detail-text sap-detail-text--muted'>&nbsp;·&nbsp;{shift.location}</span>
            </div>

            <div className='sap-detail-item'>
                <span className='sap-detail-icon'><Calendar size={9} /></span>
                <span className='sap-detail-text'>{shift.dateRange}</span>
            </div>
        </div>

        {/* Notes */}
        <div className='sap-notes'>
            <div className='sap-notes-label'>Notes for Worker</div>
            <div
                data-demo-target='approval-notes-textarea'
                className={`sap-notes-field${isNotesFocused ? ' sap-notes-field--focused' : ''}`}
            >
                <span className='sap-notes-text'>{typedNotes}</span>
                {isNotesFocused && <span className='sap-cursor-blink' />}
            </div>
        </div>

        {/* Buttons */}
        <div className='sap-actions'>
            <button className='sap-btn sap-btn--deny'>
                <X size={10} />
                Deny
            </button>
            <button
                data-demo-target='approval-approve-btn'
                className='sap-btn sap-btn--approve'
            >
                <Check size={10} />
                Approve
            </button>
        </div>

        {/* Success overlay */}
        <div className={`sap-success${showSuccess ? ' sap-success--visible' : ''}`}>
            <svg className='sap-check-svg' viewBox='0 0 56 56'>
                {showSuccess && (
                    <>
                        <circle
                            className='sap-check-bg'
                            cx='28' cy='28' r='26'
                            fill='#dcfce7'
                        />
                        <circle
                            className='sap-check-ring'
                            cx='28' cy='28' r='26'
                            fill='none'
                            stroke='#16a34a'
                            strokeWidth='2.5'
                        />
                        <path
                            className='sap-check-path'
                            d='M16 28l8 8 16-16'
                            fill='none'
                            stroke='#16a34a'
                            strokeWidth='3.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </>
                )}
            </svg>
            <span className='sap-success-label'>Shift Approved!</span>
            <span className='sap-success-sub'>{worker.name} has been notified</span>
        </div>

    </div>
);

export { ShiftApprovalPanel };
export type { ShiftApprovalWorker, ShiftApprovalShift };
